/**
 * Schedule Label Pill
 * A label (or status) as a tinted pill: the label color lightly over white with readable
 * same-hue text, no dot. Opaque, so it reads on light and dark sections alike.
 */

import type React from "react";
import { useMemo } from "react";

import { getScheduleLabelTint } from "../../../../_logic/utils/schedule-label-colors";

interface ScheduleLabelPillProps {
  /** The pill's hex color (a label's color, or a status color). Null uses the default label color. */
  color: string | null;
  children: React.ReactNode;
}

/**
 * Renders one tinted label pill.
 *
 * @param props - See ScheduleLabelPillProps.
 * @returns The pill element.
 */
export function ScheduleLabelPill({ color, children }: ScheduleLabelPillProps): React.ReactElement {
  const tint = useMemo(() => getScheduleLabelTint(color), [color]);
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold leading-5"
      style={{ backgroundColor: tint.background, color: tint.text }}
    >
      {children}
    </span>
  );
}
